import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Minus, ShoppingBag, Check } from "lucide-react";
import { api, eur } from "../lib/api";
import { LightArt } from "../components/LightArt";
import { Navigation } from "../components/Navigation";
import { useCart } from "../context/CartContext";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    document.title = "Werk · Die Lichtmalerin";
    window.scrollTo(0, 0);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAdd = () => {
    if (!product) return;
    add(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090909]">
        <Navigation />
        <p className="pt-40 text-center text-[#A1A1AA] font-sans">Lade…</p>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="min-h-screen bg-[#090909]">
        <Navigation />
        <div className="pt-40 text-center">
          <p className="text-[#E5E5E5] font-serif text-2xl">
            Werk nicht gefunden.
          </p>
          <Link
            to="/shop"
            className="mt-6 inline-block text-[#D4AF37] hover:text-[#FDE047] uppercase tracking-[0.3em] text-xs font-sans"
          >
            Zurück zum Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="product-detail-page">
      <Navigation />

      <section className="pt-28 md:pt-36 pb-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors group"
            data-testid="product-back"
          >
            <ArrowLeft
              size={14}
              strokeWidth={1.4}
              className="group-hover:-translate-x-1 transition-transform"
            />
            Zurück
          </button>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, filter: "blur(20px) brightness(0.2)" }}
              animate={{ opacity: 1, filter: "blur(0px) brightness(1)" }}
              transition={{ duration: 1.6 }}
              className="relative aspect-[4/5] border border-[#D4AF37]/15 overflow-hidden"
              style={{ boxShadow: "0 0 60px rgba(212,175,55,0.10)" }}
            >
              {product.image_base64 ? (
                <img
                  src={product.image_base64}
                  alt={product.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <LightArt variant={3} />
              )}
            </motion.div>

            {/* Details */}
            <div className="lg:pt-8">
              <span className="gold-divider">Lichtwerk</span>
              <h1
                data-testid="product-title"
                className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#E5E5E5] mt-8"
              >
                {product.title}
              </h1>
              {product.subtitle && (
                <p className="mt-3 text-xs uppercase tracking-[0.35em] text-[#D4AF37]/80 font-sans">
                  {product.subtitle}
                </p>
              )}

              <p
                data-testid="product-price"
                className="mt-8 font-serif text-4xl text-[#D4AF37]"
              >
                {eur(product.price_eur)}
              </p>
              <p className="mt-2 text-xs text-[#A1A1AA] font-sans">
                zzgl. Versand {eur(product.shipping_eur)} · inkl. MwSt.
              </p>

              <p
                data-testid="product-description"
                className="mt-8 font-sans font-light text-base text-[#E5E5E5]/85 leading-relaxed max-w-lg"
              >
                {product.description}
              </p>

              {product.stock <= 0 ? (
                <p className="mt-10 text-[#FDE047] font-serif italic">
                  Derzeit nicht verfügbar.
                </p>
              ) : (
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <div className="inline-flex items-center border border-[#D4AF37]/25">
                    <button
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="px-4 py-3 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                      data-testid="product-qty-minus"
                    >
                      <Minus size={14} />
                    </button>
                    <span
                      className="w-12 text-center font-serif text-lg text-[#E5E5E5]"
                      data-testid="product-qty"
                    >
                      {qty}
                    </span>
                    <button
                      onClick={() =>
                        setQty((q) => Math.min(product.stock, q + 1))
                      }
                      className="px-4 py-3 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                      data-testid="product-qty-plus"
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  <button
                    onClick={handleAdd}
                    data-testid="product-add-to-cart"
                    className="btn-gold inline-flex items-center gap-3 px-8 py-3 font-sans font-light tracking-widest text-xs uppercase"
                  >
                    {added ? (
                      <>
                        <Check size={16} strokeWidth={1.4} /> Hinzugefügt
                      </>
                    ) : (
                      <>
                        <ShoppingBag size={16} strokeWidth={1.4} /> In den Warenkorb
                      </>
                    )}
                  </button>
                </div>
              )}

              <p className="mt-10 text-xs text-[#A1A1AA] font-sans max-w-md leading-relaxed">
                14 Tage Widerrufsrecht. Widerruf jederzeit möglich —{" "}
                <Link
                  to="/widerruf"
                  className="text-[#D4AF37] underline underline-offset-4 decoration-[#D4AF37]/40 hover:decoration-[#D4AF37]"
                >
                  hier widerrufen
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
